import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { EduShell, EmptyState, StatCard, StatusBadge, STUDENT_NAV } from "@/components/EduShell";
import { useAuth } from "@/hooks/useAuth";
import { StudyAllowance } from "@/components/StudyAllowance";
import { useEducation } from "@/hooks/useEducation";
import { eduRpc, formatDue, isOverdue } from "@/lib/education";

import { T } from "@/components/T";
import i18n from "@/i18n";
export const Route = createFileRoute("/student")({
  component: StudentDashboard,
  head: () => ({
    meta: [
      { title: "My classroom - Inquira" },
      {
        name: "description",
        content: "See your classes, what is due next, the work you submitted and the grades your teacher returned.",
      },
      { property: "og:title", content: "My Inquira classroom" },
      { property: "og:description", content: "Classes, deadlines, submissions and grades in one place." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
    ],
  }),
});

interface Overview {
  classes: Array<{ id: string; name: string; subject: string | null; grade_level: string | null }>;
  due_soon: number;
  submitted: number;
  average_score: number | null;
  assignments: Array<{
    id: string;
    title: string;
    class_name: string | null;
    kind: string | null;
    due_at: string | null;
    status: string;
    score: number | null;
    points: number | null;
    feedback: string | null;
  }>;
  events: Array<{ id: string; title: string; kind: string | null; starts_at: string }>;
  unread_notifications: number;
}

function StudentDashboard() {
  const { user, isLoading } = useAuth();
  const { teacherPortalEnabled, loading } = useEducation();
  const [data, setData] = useState<Overview | null>(null);

  useEffect(() => {
    if (!user) return;
    void eduRpc<Overview>("edu_student_overview").then(({ data }) => setData(data));
  }, [user]);

  if (isLoading || loading) return null;

  if (!user) {
    return (
      <EduShell nav={STUDENT_NAV} title={i18n.t("edu.student.my_classroom")}>
        <EmptyState title={i18n.t("edu.student.sign_in_first")} body="Sign in to see your classes and assignments." />
        <Link to="/login" className="mt-4 inline-block rounded-full bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground">
          Sign in
        </Link>
      </EduShell>
    );
  }

  if (!teacherPortalEnabled) {
    return (
      <EduShell nav={STUDENT_NAV} title={i18n.t("edu.student.my_classroom")} subtitle={i18n.t("edu.student.this_area_is_still_in_testing")}>
        <p className="text-sm text-ink-muted">
          Classroom features are not switched on for your account yet. Your study tools work as normal.
        </p>
        <Link to="/" className="mt-4 inline-block text-sm font-semibold text-neon-pink"><T k="edu.student.back_to_inquira" /></Link>
      </EduShell>
    );
  }

  const assignments = data?.assignments ?? [];
  const todo = assignments.filter((a) => ["not_started", "in_progress"].includes(a.status)).slice(0, 6);
  const graded = assignments.filter((a) => ["graded", "returned"].includes(a.status)).slice(0, 6);

  return (
    <EduShell
      nav={STUDENT_NAV}
      title={i18n.t("edu.student.my_classroom")}
      subtitle={i18n.t("edu.student.what_is_due_next_what_you_handed_in_and_")}
      actions={
        <Link to="/classes" search={{ join: undefined }} className="rounded-full bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground">
          Join a class
        </Link>
      }
    >
      <StudyAllowance />
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <StatCard label={i18n.t("edu.student.classes")} value={data ? data.classes.length : "—"} />
        <StatCard label={i18n.t("edu.student.due_this_week")} value={data?.due_soon ?? "—"} />
        <StatCard label={i18n.t("edu.student.handed_in")} value={data?.submitted ?? "—"} />
        <StatCard label={i18n.t("edu.student.average_score")} value={data?.average_score ?? "—"} />
      </div>

      <section className="mt-8 grid gap-6 lg:grid-cols-2">
        <div>
          <h2 className="mb-3 font-display text-lg font-bold text-ink">To do</h2>
          {todo.length === 0 ? (
            <EmptyState title={i18n.t("edu.student.nothing_due")} body="You are all caught up." />
          ) : (
            <ul className="space-y-2">
              {todo.map((a) => (
                <li key={a.id} className="rounded-2xl border border-rule bg-card p-3">
                  <Link to="/assignments/$assignmentId" params={{ assignmentId: a.id }} className="block">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <span className="font-semibold text-ink">{a.title}</span>
                      <span className={isOverdue(a.due_at, a.status) ? "text-xs font-semibold text-destructive" : "text-xs text-ink-muted"}>
                        {formatDue(a.due_at)}
                      </span>
                    </div>
                    <p className="text-sm text-ink-muted">{[a.class_name, a.kind].filter(Boolean).join(" · ")}</p>
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="space-y-6">
          <div>
            <h2 className="mb-3 font-display text-lg font-bold text-ink"><T k="edu.student.grades" /></h2>
            {graded.length === 0 ? (
              <EmptyState title={i18n.t("edu.student.no_grades_yet")} body="Marked work shows up here with your teacher's feedback." />
            ) : (
              <ul className="space-y-2">
                {graded.map((a) => (
                  <li key={a.id} className="rounded-2xl border border-rule bg-card p-3">
                    <Link to="/assignments/$assignmentId" params={{ assignmentId: a.id }} className="block">
                      <div className="flex flex-wrap items-center justify-between gap-2">
                        <span className="font-semibold text-ink">{a.title}</span>
                        <span className="flex items-center gap-2 text-xs">
                          <StatusBadge status={a.status} />
                          <span className="font-semibold text-ink">
                            {a.score !== null ? `${a.score}${a.points ? ` / ${a.points}` : ""}` : "—"}
                          </span>
                        </span>
                      </div>
                      {a.feedback && <p className="mt-1 text-sm text-ink-muted">{a.feedback}</p>}
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div>
            <h2 className="mb-3 font-display text-lg font-bold text-ink"><T k="edu.student.coming_up" /></h2>
            {(data?.events ?? []).length === 0 ? (
              <EmptyState title={i18n.t("edu.student.nothing_on_the_calendar")} />
            ) : (
              <ul className="space-y-2">
                {data!.events.slice(0, 6).map((e) => (
                  <li key={e.id} className="flex items-center justify-between rounded-2xl border border-rule bg-card p-3">
                    <span className="font-semibold text-ink">{e.title}</span>
                    <span className="text-xs text-ink-muted">{formatDue(e.starts_at)}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </section>

      <section className="mt-8">
        <h2 className="mb-3 font-display text-lg font-bold text-ink"><T k="edu.student.my_classes" /></h2>
        {(data?.classes ?? []).length === 0 ? (
          <EmptyState title={i18n.t("edu.student.no_classes_yet")} body="Ask your teacher for a class code, then join from My classes." />
        ) : (
          <ul className="grid gap-2 sm:grid-cols-2">
            {data!.classes.map((c) => (
              <li key={c.id} className="rounded-2xl border border-rule bg-card p-3">
                <p className="font-semibold text-ink">{c.name}</p>
                <p className="text-sm text-ink-muted">{[c.subject, c.grade_level].filter(Boolean).join(" · ")}</p>
              </li>
            ))}
          </ul>
        )}
      </section>
    </EduShell>
  );
}
